import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  History, Filter, Download, RefreshCw, Loader2, Search,
  ShieldCheck, FileText, Package, ScrollText, Scale, Archive, Bell, Mail, X,
} from "lucide-react";
import "./AdminEmailDelivery.css";

const MODULE_META = {
  email_template: { label: "Email Templates", icon: Mail },
  trust_center_page: { label: "Trust Center Pages", icon: FileText },
  trust_center_document: { label: "Trust Center Documents", icon: Package },
  release_note: { label: "Release Notes", icon: ScrollText },
  regulatory_framework: { label: "Regulatory Frameworks", icon: ShieldCheck },
  compliance_setting: { label: "Compliance Settings", icon: Scale },
  legal_hold: { label: "Legal Holds", icon: Archive },
  retention_policy: { label: "Retention Policies", icon: Archive },
  trust_announcement: { label: "Policies / ToS / Privacy / BAA / DPA", icon: Bell },
};

const CHANGE_TYPE_TONE = {
  create: "verified", update: "neutral", submit: "warning", approve: "verified",
  reject: "error", publish: "verified", archive: "neutral", rollback: "warning",
  release: "verified",
};

const STATUS_TONE = {
  published: "verified", approved: "verified", in_review: "warning",
  draft: "neutral", archived: "neutral", released: "verified", active: "warning",
};

function Pill({ tone = "neutral", children, testid }) {
  return <span className={`email-infra-pill pill-${tone}`} data-testid={testid}>{children}</span>;
}

function pretty(ts) { return ts ? new Date(ts).toLocaleString() : "—"; }
function truncate(s, n = 100) { return !s ? "" : (s.length <= n ? s : s.slice(0, n) + "…"); }
/** Defensive coercion — some legacy actor fields may sneak through as
 * {id,email,name} objects. Never render a raw object as a React child. */
function actorStr(v) {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.email || v.name || v.id || "—";
  return String(v);
}

function ModuleSummary({ modules, active, onToggle }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginBottom: 16 }}>
      {modules.map(m => {
        const meta = MODULE_META[m.module] || { label: m.label };
        const Icon = meta.icon || History;
        const isActive = active.includes(m.module);
        return (
          <button key={m.module} onClick={() => onToggle(m.module)} data-testid={`ch-module-${m.module}`}
                  style={{
                    background: isActive ? "#1E3A8A" : "#fff",
                    color: isActive ? "#fff" : "#0F172A",
                    border: `1px solid ${isActive ? "#1E3A8A" : "#E5E5E0"}`,
                    borderRadius: 12, padding: "10px 12px", textAlign: "left", cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700,
                              textTransform: "uppercase", letterSpacing: "0.06em",
                              color: isActive ? "#F59E0B" : "#64748B" }}>
              <Icon size={13} /> {meta.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{m.count}</div>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------

function DetailDrawer({ record, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!record) return;
    setDetail(null);
    api.get(`/admin/change-history/detail/${record.module}/${record.item_id}`)
      .then(({ data }) => setDetail(data))
      .catch(e => toast.error(formatApiError(e)));
  }, [record]);
  if (!record) return null;
  return (
    <>
      <div className="email-infra-drawer-backdrop" onClick={onClose} />
      <div className="email-infra-drawer" data-testid="ch-detail-drawer">
        <button className="email-infra-drawer__close" onClick={onClose}><X size={18} /></button>
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
                        color: "#F59E0B", fontWeight: 700 }}>
          {MODULE_META[record.module]?.label || record.module}
        </div>
        <h2 style={{ margin: "4px 0", fontSize: 20, color: "#0F172A" }}>{record.item_name}</h2>

        {!detail ? (
          <div style={{ padding: 20 }}><Loader2 size={18} className="animate-spin" /></div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              <Pill tone={CHANGE_TYPE_TONE[detail.change_type] || "neutral"}>{detail.change_type}</Pill>
              <Pill tone={STATUS_TONE[detail.status] || "neutral"}>{detail.status}</Pill>
              {detail.version != null ? <Pill tone="neutral">v{detail.version}</Pill> : null}
              {detail.current_version != null ? <Pill tone="verified">current v{detail.current_version}</Pill> : null}
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title">Governance</div>
              <table className="email-infra-table">
                <tbody>
                  <tr><th>Created by</th><td>{actorStr(detail.created_by)}</td></tr>
                  <tr><th>Reviewed by</th><td>{actorStr(detail.reviewed_by)}</td></tr>
                  <tr><th>Approved by</th><td>{actorStr(detail.approved_by)}</td></tr>
                  <tr><th>Published by</th><td>{actorStr(detail.published_by)}</td></tr>
                  <tr><th>Created</th><td>{pretty(detail.created_at)}</td></tr>
                  <tr><th>Approved</th><td>{pretty(detail.approved_at)}</td></tr>
                  <tr><th>Published</th><td>{pretty(detail.published_at)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title">Change summary</div>
              <p style={{ fontSize: 13, color: "#334155", marginTop: 0 }} data-testid="ch-change-summary">
                {detail.change_summary || <em style={{ color: "#94A3B8" }}>No summary recorded.</em>}
              </p>
              {detail.business_justification ? (
                <div style={{ marginTop: 8, padding: 10, background: "#F0F5FF", borderRadius: 8, fontSize: 12 }}>
                  <strong>Business justification:</strong> {detail.business_justification}
                </div>
              ) : null}
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title">Digital integrity</div>
              <code style={{ fontSize: 11, wordBreak: "break-all" }} data-testid="ch-integrity-hash">
                {detail.digital_integrity_hash}
              </code>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 6 }}>
                SHA-256 over the record&apos;s identifying fields. Use this to prove the record&apos;s
                contents at export time.
              </div>
            </div>

            <div className="email-infra-card">
              <div className="email-infra-card__title">Linked audit log ({detail.audit_log_entries?.length || 0})</div>
              {detail.audit_log_entries?.length ? (
                <table className="email-infra-table">
                  <thead><tr><th>Time</th><th>Action</th><th>Actor</th></tr></thead>
                  <tbody>
                    {detail.audit_log_entries.map(a => (
                      <tr key={a.id}>
                        <td style={{ fontSize: 11 }}>{pretty(a.ts)}</td>
                        <td><code>{a.action}</code></td>
                        <td>{a.actor_email || a.actor?.email || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p style={{ fontSize: 12, color: "#94A3B8" }}>No audit entries linked.</p>}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------

export default function AdminChangeHistory() {
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sinceDays, setSinceDays] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const loadModules = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/change-history/modules");
      setModules(data.modules || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "150" });
      if (selectedModules.length) params.set("modules", selectedModules.join(","));
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (sinceDays) params.set("since_days", sinceDays);
      const { data } = await api.get(`/admin/change-history/?${params}`);
      setRows(data.items || []); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selectedModules, q, status, sinceDays]);

  useEffect(() => { loadModules(); load(); }, [loadModules, load]);

  const toggleModule = (m) => {
    setSelectedModules(cur => cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m]);
  };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({ limit: "1000" });
      if (selectedModules.length) params.set("modules", selectedModules.join(","));
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (sinceDays) params.set("since_days", sinceDays);
      const res = await api.get(`/admin/change-history/export.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "change_history.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="email-infra-shell" data-testid="admin-change-history">
      <div className="email-infra-hero">
        <div className="email-infra-hero__eyebrow">Governance · Change History</div>
        <h1 className="email-infra-hero__title">Enterprise Change History</h1>
        <div className="email-infra-hero__sub">
          Unified governance timeline across email templates, Trust Center pages &amp; documents,
          release notes, regulatory frameworks, compliance settings, legal holds, retention
          policies, and platform policies (ToS / Privacy / BAA / DPA). Live-aggregated from
          every source-of-truth collection.
        </div>
      </div>

      <ModuleSummary modules={modules} active={selectedModules} onToggle={toggleModule} />

      <div className="email-infra-controls">
        <input placeholder="Search names / summaries / IDs…" value={q}
                onChange={e => setQ(e.target.value)} data-testid="ch-q-input" />
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="ch-status-select">
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="approved">Approved</option>
          <option value="in_review">In review</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
          <option value="active">Active</option>
          <option value="released">Released</option>
        </select>
        <select value={sinceDays} onChange={e => setSinceDays(e.target.value)} data-testid="ch-days-select">
          <option value="">All time</option>
          <option value="1">Last 24h</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
        <button className="email-infra-btn email-infra-btn--primary" onClick={load} disabled={loading}
                data-testid="ch-search-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
        </button>
        <button className="email-infra-btn email-infra-btn--secondary" onClick={downloadCsv}
                data-testid="ch-export-btn">
          <Download size={14} /> CSV
        </button>
        {selectedModules.length ? (
          <button className="email-infra-btn email-infra-btn--secondary" onClick={() => setSelectedModules([])}
                  data-testid="ch-clear-modules">
            Clear module filter
          </button>
        ) : null}
      </div>

      <div className="email-infra-card">
        <div className="email-infra-card__title">
          <History size={16} /> Records ({rows.length} of {total})
        </div>
        <table className="email-infra-table" data-testid="ch-table">
          <thead>
            <tr>
              <th>Module</th><th>Item</th><th>Version</th>
              <th>Change</th><th>Status</th>
              <th>Approved by</th><th>Published by</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} style={{ color: "#64748B" }}>No records matched.</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={r.id} data-testid={`ch-row-${idx}`}
                  onClick={() => setDetailRecord(r)} style={{ cursor: "pointer" }}>
                <td>
                  <Pill tone="neutral">{MODULE_META[r.module]?.label || r.module}</Pill>
                </td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{truncate(r.item_name, 70)}</div>
                  {r.change_summary ? (
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                      {truncate(r.change_summary, 90)}
                    </div>
                  ) : null}
                </td>
                <td>{r.version ?? "—"}</td>
                <td><Pill tone={CHANGE_TYPE_TONE[r.change_type] || "neutral"}>{r.change_type}</Pill></td>
                <td><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
                <td style={{ fontSize: 12 }}>{actorStr(r.approved_by)}</td>
                <td style={{ fontSize: 12 }}>{actorStr(r.published_by)}</td>
                <td style={{ fontSize: 11, color: "#64748B" }}>
                  {pretty(r.published_at || r.approved_at || r.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DetailDrawer record={detailRecord} onClose={() => setDetailRecord(null)} />
    </div>
  );
}
